/**
 * Canonical Event Taxonomy
 *
 * Single source of truth for all analytics events.
 * Events marked with MIRROR_TO_DB are also logged to moonscape.user_activity table.
 *
 * @see docs/design/user-events-tracking.md
 */

// ============================================================================
// EVENT STATUS (for server-side activity tracking)
// ============================================================================
export const ACTIVITY_STATUS = {
  ATTEMPT: "attempt",
  SUCCESS: "success",
  FAILED: "failed",
  VIEWED: "viewed",
} as const;

export type ActivityStatus = (typeof ACTIVITY_STATUS)[keyof typeof ACTIVITY_STATUS];

// ============================================================================
// EVENTS THAT MIRROR TO OUR DB (moonscape.user_activity)
// These are critical for conversion tracking and debugging
// ============================================================================
export const MIRRORED_EVENTS = {
  // Authentication - track login/signup attempts
  LOGIN_SUCCEEDED: "login_succeeded",
  LOGIN_FAILED: "login_failed",
  SIGNUP_COMPLETED: "signup_completed",

  // Onboarding milestones
  ONBOARDING_STEP_TRANSITION: "onboarding_step_transition",
  ONBOARDING_COMPLETED: "onboarding_completed",

  // Exchange connection (critical conversion)
  EXCHANGE_API_CONNECTED: "exchange_api_connected",
  EXCHANGE_CONNECTION_TEST_STARTED: "exchange_connection_test_started",
  EXCHANGE_CONNECTION_TEST_PASSED: "exchange_connection_test_passed",
  EXCHANGE_CONNECTION_TEST_FAILED: "exchange_connection_test_failed",

  // CSV import
  CSV_IMPORT_STARTED: "csv_import_started",
  CSV_IMPORT_COMPLETED: "csv_import_completed",
  CSV_IMPORT_FAILED: "csv_import_failed",

  // Sync (server-side only)
  SYNC_STARTED: "sync_started",
  SYNC_COMPLETED: "sync_completed",
  SYNC_FAILED: "sync_failed",

  // Tax calculation (server-side only)
  TAX_CALC_STARTED: "tax_calc_started",
  TAX_CALC_COMPLETED: "tax_calc_completed",
  TAX_CALC_FAILED: "tax_calc_failed",

  // Tax reports
  TAX_REPORT_GENERATED: "tax_report_generated",
  TAX_REPORT_DOWNLOADED: "tax_report_downloaded",
  TAX_REPORT_ERROR: "tax_report_error",

  // Subscription (revenue)
  PAYWALL_SHOWN: "paywall_shown",
  UPGRADE_INITIATED: "upgrade_initiated",
  SUBSCRIPTION_STARTED: "subscription_started",
  SUBSCRIPTION_COMPLETED: "subscription_completed",
} as const;

export type MirroredEvent = (typeof MIRRORED_EVENTS)[keyof typeof MIRRORED_EVENTS];

/**
 * Check if event should be mirrored to our DB
 */
export const shouldMirrorEvent = (eventName: string): boolean => {
  return Object.values(MIRRORED_EVENTS).includes(eventName as MirroredEvent);
};

// ============================================================================
// REQUIRED METADATA FOR MIRRORED EVENTS
// ============================================================================
export interface MirroredEventMetadata {
  // Required for all
  platform: "web" | "mobile" | "ios" | "server";
  app_version?: string;

  // For exchange events
  exchange?: string;

  // For CSV events
  filename?: string;

  // For failure events
  error_code?: string;
  error_message?: string;

  // For duration tracking
  duration_ms?: number;
}

// ============================================================================
// ALL EVENTS BY CATEGORY (PostHog/Segment only unless in MIRRORED_EVENTS)
// ============================================================================

// Authentication Events
export const AUTH_EVENTS = {
  LOGIN_SCREEN_VIEWED: "login_screen_viewed",
  LOGIN_ATTEMPT_STARTED: "login_attempt_started",
  LOGIN_SUCCEEDED: MIRRORED_EVENTS.LOGIN_SUCCEEDED,
  LOGIN_FAILED: MIRRORED_EVENTS.LOGIN_FAILED,
} as const;

// Onboarding Events
export const ONBOARDING_EVENTS = {
  ONBOARDING_SCREEN_VIEWED: "onboarding_screen_viewed",
  STEP_TRANSITION: MIRRORED_EVENTS.ONBOARDING_STEP_TRANSITION,
  STEP_SKIPPED: "onboarding_step_skipped",
  EXCHANGE_SELECTED: "onboarding_exchange_selected",
  EXCHANGE_SEARCH: "exchange_search",
  EXCHANGE_CONNECTION_METHOD_SELECTED: "exchange_connection_method_selected",
  EXCHANGE_API_CONNECTED: MIRRORED_EVENTS.EXCHANGE_API_CONNECTED,
  EXCHANGE_CSV_UPLOADED: "exchange_csv_uploaded",
  EXCHANGE_INTEREST_REGISTERED: "exchange_interest_registered",
  MILESTONE_REACHED: "onboarding_milestone_reached",
  COMPLETED: MIRRORED_EVENTS.ONBOARDING_COMPLETED,
  TAX_COUNTRY_SELECTED: "tax_country_selected",
  TAX_SETTINGS_UPDATED: "tax_settings_updated",
  USER_ATTRIBUTES_UPDATED: "user_attributes_updated",
  SUPPORT_CTA_CLICKED: "support_cta_clicked",
} as const;

// Settings Events
export const SETTINGS_EVENTS = {
  SETTINGS_SCREEN_VIEWED: "settings_screen_viewed",
  TAX_JURISDICTION_CHANGED: "tax_jurisdiction_changed",
  CURRENCY_PREFERENCE_CHANGED: "currency_preference_changed",
  LANGUAGE_PREFERENCE_CHANGED: "language_preference_changed",
  NOTIFICATION_PREFERENCE_CHANGED: "notification_preference_changed",
  TAX_SETTINGS_OPENED: "tax_settings_opened",
  TAX_SETTINGS_UPDATED: "tax_settings_updated",
  EXCHANGE_MODAL_OPENED: "exchange_modal_opened",
  EXCHANGE_SELECTED: "exchange_selected",
  EXCHANGE_SEARCH: "exchange_search",
  EXCHANGE_CONNECTION_METHOD_SELECTED: "exchange_connection_method_selected",
  EXCHANGE_API_CONNECTED: MIRRORED_EVENTS.EXCHANGE_API_CONNECTED,
  EXCHANGE_CSV_UPLOADED: "exchange_csv_uploaded",
  EXCHANGE_INTEREST_REGISTERED: "exchange_interest_registered",
} as const;

// Subscription Events
export const SUBSCRIPTION_EVENTS = {
  PAYWALL_SHOWN: MIRRORED_EVENTS.PAYWALL_SHOWN,
  UPGRADE_INITIATED: MIRRORED_EVENTS.UPGRADE_INITIATED,
  SUBSCRIPTION_COMPLETED: MIRRORED_EVENTS.SUBSCRIPTION_COMPLETED,
} as const;

// Conversion Events (for ad platforms)
export const CONVERSION_EVENTS = {
  SIGN_UP: MIRRORED_EVENTS.SIGNUP_COMPLETED,
  EXCHANGE_CONNECTED: MIRRORED_EVENTS.EXCHANGE_API_CONNECTED,
  TAX_REPORT_GENERATED: MIRRORED_EVENTS.TAX_REPORT_GENERATED,
  SUBSCRIPTION_STARTED: MIRRORED_EVENTS.SUBSCRIPTION_STARTED,
  SUBSCRIPTION_PAID: MIRRORED_EVENTS.SUBSCRIPTION_COMPLETED,
  ONBOARDING_COMPLETED: MIRRORED_EVENTS.ONBOARDING_COMPLETED,
  // Upsell flow events
  UPSELL_BANNER_CLICKED: "upsell_banner_clicked",
  UPSELL_BANNER_DISMISSED: "upsell_banner_dismissed",
  EXIT_INTENT_SHOWN: "exit_intent_shown",
  EXIT_INTENT_DISMISSED: "exit_intent_dismissed",
  EXIT_INTENT_CONVERTED: "exit_intent_converted",
  // Quiz upsell events
  PURCHASE_INITIATED: "purchase_initiated",
} as const;

// Exchange Management Events
export const EXCHANGE_MANAGEMENT_EVENTS = {
  EXCHANGE_ADD_STARTED: "exchange_add_started",
  EXCHANGE_ADD_METHOD_SELECTED: "exchange_add_method_selected",
  EXCHANGE_CONNECTION_TEST_STARTED: MIRRORED_EVENTS.EXCHANGE_CONNECTION_TEST_STARTED,
  EXCHANGE_CONNECTION_TEST_PASSED: MIRRORED_EVENTS.EXCHANGE_CONNECTION_TEST_PASSED,
  EXCHANGE_CONNECTION_TEST_FAILED: MIRRORED_EVENTS.EXCHANGE_CONNECTION_TEST_FAILED,
  EXCHANGE_ADD_COMPLETED: "exchange_add_completed",
  EXCHANGE_DELETED: "exchange_deleted",
} as const;

// Transaction Discovery Events
export const TRANSACTION_DISCOVERY_EVENTS = {
  TRANSACTION_LIST_VIEWED: "transaction_list_viewed",
  TRANSACTION_DETAIL_VIEWED: "transaction_detail_viewed",
  TRANSACTION_SEARCH_INITIATED: "transaction_search_initiated",
  TRANSACTION_SEARCH_RESULTS: "transaction_search_results",
  TRANSACTION_FILTER_APPLIED: "transaction_filter_applied",
  TRANSACTION_FILTER_CLEARED: "transaction_filter_cleared",
} as const;

// Navigation Events
export const NAVIGATION_EVENTS = {
  SCREEN_VIEWED: "screen_viewed",
  LOGOUT_INITIATED: "logout_initiated",
  LOGOUT_COMPLETED: "logout_completed",
} as const;

// Web-Specific Events
export const WEB_SPECIFIC_EVENTS = {
  THEME_TOGGLED: "theme_toggled",
  MODAL_OPENED: "modal_opened",
  MODAL_DISMISSED: "modal_dismissed",
  CSV_IMPORT_STARTED: MIRRORED_EVENTS.CSV_IMPORT_STARTED,
} as const;

// Tax Feature Events
export const TAX_FEATURE_EVENTS = {
  /**
   * ACTIVATION north star, shared verbatim with iOS
   * (NewIAFirstNumberScreen / FCTaxNumberView): the user saw an insight built
   * from their own data. Properties: insight_type (tax_estimate | tax_empty |
   * blockers), has_value, unlocked, surface, year.
   * @see docs/reports/investigations/activation-events-instrumentation.md
   */
  INSIGHT_VIEWED: "insight_viewed",
  TAX_SETTINGS_COMPLETED: "tax_settings_completed",
  TAX_REPORT_GENERATED: MIRRORED_EVENTS.TAX_REPORT_GENERATED,
  TAX_REPORT_DOWNLOADED: MIRRORED_EVENTS.TAX_REPORT_DOWNLOADED,
  TAX_REPORT_ERROR: MIRRORED_EVENTS.TAX_REPORT_ERROR,
} as const;

// Feature Flag & A/B Testing Events
export const FEATURE_FLAG_EVENTS = {
  FEATURE_FLAG_EVALUATED: "feature_flag_evaluated",
  VARIANT_ASSIGNED: "variant_assigned",
} as const;

// Monetization Events
export const MONETIZATION_EVENTS = {
  FEATURE_UNLOCK_ATTEMPTED: "feature_unlock_attempted",
  FEATURE_UNLOCK_BLOCKED: "feature_unlock_blocked",
  PLAN_DOWNGRADE_INITIATED: "plan_downgrade_initiated",
} as const;

// Error Handling & Resilience Events
export const ERROR_RESILIENCE_EVENTS = {
  API_REQUEST_FAILED: "api_request_failed",
  NETWORK_ERROR: "network_error",
  SYNC_FAILED: MIRRORED_EVENTS.SYNC_FAILED,
  ERROR_RETRY_INITIATED: "error_retry_initiated",
  ERROR_RETRY_SUCCEEDED: "error_retry_succeeded",
  CRITICAL_ERROR_OCCURRED: "critical_error_occurred",
  SESSION_ERROR_COUNT: "session_error_count",
} as const;

// Form Interaction Events
export const FORM_INTERACTION_EVENTS = {
  FORM_FIELD_FOCUSED: "form_field_focused",
  FORM_FIELD_CHANGED: "form_field_changed",
  FORM_VALIDATION_FAILED: "form_validation_failed",
  FORM_SUBMITTED: "form_submitted",
} as const;

// Chart Interaction Events
export const CHART_INTERACTION_EVENTS = {
  CHART_VIEWED: "chart_viewed",
  CHART_TIMEFRAME_CHANGED: "chart_timeframe_changed",
  CHART_EXPORT_CLICKED: "chart_export_clicked",
} as const;

// CSV Events
export const CSV_EVENTS = {
  CSV_IMPORT_STARTED: MIRRORED_EVENTS.CSV_IMPORT_STARTED,
  CSV_IMPORT_COMPLETED: MIRRORED_EVENTS.CSV_IMPORT_COMPLETED,
  CSV_IMPORT_FAILED: MIRRORED_EVENTS.CSV_IMPORT_FAILED,
  CSV_EXPORT_CLICKED: "csv_export_clicked",
} as const;

// Session Events
export const SESSION_EVENTS = {
  SESSION_STARTED: "session_started",
  SESSION_ENDED: "session_ended",
} as const;

// Accountant Events
export const ACCOUNTANT_EVENTS = {
  CLIENT_ADDED: "accountant_client_added",
  CLIENT_INVITE_SENT: "accountant_client_invite_sent",
  CLIENT_INVITE_ACCEPTED: "accountant_client_invite_accepted",
  VIEWING_CLIENT_DATA: "accountant_viewing_client_data",
  CONTEXT_SWITCHED: "accountant_context_switched",
  ACCOUNT_TYPE_CHANGED: "account_type_changed",
} as const;

// ============================================================================
// META PIXEL MAPPING (for ad attribution)
// ============================================================================
export const META_STANDARD_EVENT_MAPPING: Record<string, string> = {
  [CONVERSION_EVENTS.SIGN_UP]: "CompleteRegistration",
  [CONVERSION_EVENTS.EXCHANGE_CONNECTED]: "Lead",
  [CONVERSION_EVENTS.SUBSCRIPTION_STARTED]: "StartTrial",
  [CONVERSION_EVENTS.SUBSCRIPTION_PAID]: "Subscribe",
};

export const EVENT_CATEGORIES = {
  ONBOARDING: "onboarding",
  SETTINGS: "settings",
} as const;
